import React, { useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import PageShell from '../ui/PageShell';
import { PageHeader } from '../ui/PageHeader';
import PageBackLink from '../ui/PageBackLink';
import { Button } from '../ui/Button';
import apiService from '../../services/ApiService';
import SettingsSubnav from '../settings/SettingsSubnav';
import { isRunningInBiltNativeShell } from '../../utils/nativeShell';

type Plan = {
  plan_key: string;
  label: string;
  interval_unit: string;
  amount_cents: number;
  preferred?: boolean;
};

/**
 * Bilt:Spec Premium account / billing surface.
 */
const BillingSettingsPage: React.FC = () => {
  const [params] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<Record<string, unknown> | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiService.getPremiumStatus();
      setStatus(data);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Could not load Premium status.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const plans = (status?.plans as Plan[] | undefined) ?? [];
  const isPremium = Boolean(status?.is_premium);
  const inNativeShell = isRunningInBiltNativeShell();
  const nativePurchaseBlocked = inNativeShell && !isPremium;
  const readiness = status?.readiness as
    | { ready?: boolean; summary?: string; checks?: Array<{ key: string; ok: boolean; label: string; detail?: string }> }
    | undefined;
  // Prefer full readiness when API provides it; otherwise fall back to product flag alone.
  const checkoutReady = readiness
    ? Boolean(readiness.ready)
    : Boolean(status?.premium_enabled);
  const failedChecks = (readiness?.checks ?? []).filter((c) => !c.ok);

  const startCheckout = async (planKey: string) => {
    setBusy(planKey);
    setError(null);
    try {
      const res = await apiService.premiumCheckout(planKey);
      if (res.checkout_url) {
        window.location.assign(res.checkout_url);
        return;
      }
      setError('Checkout did not return a URL. Confirm Stripe Price IDs are configured.');
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Checkout failed.');
    } finally {
      setBusy(null);
    }
  };

  const openPortal = async () => {
    setBusy('portal');
    try {
      const res = await apiService.premiumBillingPortal();
      if (res.url) window.location.assign(res.url);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Could not open billing portal.');
    } finally {
      setBusy(null);
    }
  };

  const formatMoney = (cents: number) =>
    new Intl.NumberFormat(undefined, { style: 'currency', currency: 'USD' }).format(cents / 100);

  return (
    <PageShell>
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8">
      <PageBackLink label="Back to Settings" to="/settings" className="mb-4" />
      <PageHeader
        eyebrow="Settings"
        title="Bilt:Spec Premium"
        description="Take better care of your build and preserve its complete story."
      />
      <SettingsSubnav />

      {params.get('premium') === 'success' ? (
        <div className="mb-4 rounded-lg border border-emerald-500/30 bg-emerald-500/10 px-4 py-3 text-sm text-emerald-100">
          Thanks. Premium will appear here once Stripe confirms your subscription.
        </div>
      ) : null}

      {error ? (
        <div role="alert" className="alert-error mb-4">
          {error}
        </div>
      ) : null}

      {loading ? (
        <div className="h-32 rounded-xl bg-[#141414]/70 animate-pulse" role="status" />
      ) : (
        <div className="space-y-6 max-w-2xl">
          <section className="card p-5">
            <h2 className="heading-card mb-2">Status</h2>
            <p className="text-sm text-zinc-300">
              {isPremium
                ? `Active${status?.status ? ` (${String(status.status)})` : ''}`
                : 'Not subscribed'}
            </p>
            {status?.period_end ? (
              <p className="mt-2 text-xs text-zinc-500">
                {status.cancel_at_period_end
                  ? `Access continues until ${String(status.period_end)}. Your vehicle history stays in your account.`
                  : `Current period ends ${String(status.period_end)}`}
              </p>
            ) : null}
            {isPremium && (status?.source === 'stripe' || status?.subscription) ? (
              <div className="mt-4">
                <Button type="button" variant="secondary" size="sm" disabled={busy === 'portal'} onClick={() => void openPortal()}>
                  Manage billing
                </Button>
              </div>
            ) : null}
          </section>

          <section className="card p-5">
            <h2 className="heading-card mb-1">Included with Premium</h2>
            <ul className="mt-3 space-y-2 text-sm text-zinc-400">
              <li>Advanced maintenance schedules and reminders</li>
              <li>Deeper documents and ownership reports</li>
              <li>More generous AI for build and maintenance work</li>
              <li>Premium presentation and export tools</li>
            </ul>
            <p className="mt-3 text-xs text-zinc-500">
              Core build documentation, Passport basics, events, QR, and Car Show Mode stay free.
              Maintenance history you log stays accessible if Premium ends.
            </p>
          </section>

          {!isPremium ? (
            <section className="card p-5">
              <h2 className="heading-card mb-3">Choose a plan</h2>
              {nativePurchaseBlocked ? (
                <div className="mb-3 rounded-lg border border-white/10 bg-[#141414]/80 px-4 py-3 text-sm text-zinc-300">
                  <p className="text-white font-medium">Bilt:Spec Premium</p>
                  <p className="mt-2">
                    Manage an existing subscription on the web. New mobile App Store purchases are not available in this
                    build yet.
                  </p>
                  <p className="mt-2 text-xs text-zinc-500">
                    Existing Premium subscribers keep full access. We will add compliant in-app purchase when ready.
                  </p>
                </div>
              ) : null}
              {!checkoutReady ? (
                <div className="mb-3 rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-sm text-amber-100">
                  <p>{readiness?.summary || 'Premium checkout is not fully configured yet.'}</p>
                  {failedChecks.length > 0 ? (
                    <ul className="mt-2 list-disc pl-5 text-xs text-amber-100/90 space-y-1">
                      {failedChecks.map((c) => (
                        <li key={c.key}>
                          {c.label}
                          {c.detail ? `: ${c.detail}` : ''}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="mt-1 text-xs text-amber-100/80">
                      Waiting on product flag and Stripe Price IDs. Admins can verify under Admin → Bilt:Spec Premium.
                    </p>
                  )}
                </div>
              ) : null}
              <ul className="space-y-3">
                {plans.map((plan) => (
                  <li
                    key={plan.plan_key}
                    className={`rounded-xl border px-4 py-3 flex flex-wrap items-center justify-between gap-3 ${
                      plan.preferred ? 'border-orange-500/40 bg-orange-500/[0.06]' : 'border-white/10'
                    }`}
                  >
                    <div>
                      <p className="text-sm text-white font-medium">
                        {plan.label}
                        {plan.preferred ? <span className="ml-2 text-xs text-orange-300">Best value</span> : null}
                      </p>
                      <p className="text-xs text-zinc-500 mt-0.5">
                        {formatMoney(plan.amount_cents)} / {plan.interval_unit}
                      </p>
                    </div>
                    {!nativePurchaseBlocked ? (
                      <Button
                        type="button"
                        size="sm"
                        disabled={!checkoutReady || busy === plan.plan_key}
                        onClick={() => void startCheckout(plan.plan_key)}
                      >
                        {busy === plan.plan_key ? 'Starting…' : 'Continue'}
                      </Button>
                    ) : null}
                  </li>
                ))}
              </ul>
            </section>
          ) : null}

          <p className="text-xs text-zinc-500">
            <Link to="/settings" className="text-orange-300 hover:underline">
              All settings
            </Link>
          </p>
        </div>
      )}
      </div>
    </PageShell>
  );
};

export default BillingSettingsPage;
